import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  message,
  Tooltip,
  Progress,
  Avatar,
  Badge,
  Tabs,
  Drawer,
  Timeline,
  InputNumber,
  Divider,
  Alert,
  Upload,
  List
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  UserOutlined,
  DownloadOutlined,
  UploadOutlined,
  ContainerOutlined,
  AuditOutlined,
  SolutionOutlined,
  FileDoneOutlined,
  FileProtectOutlined,
  ScheduleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { TextArea } = Input;

interface Contract {
  id: string;
  contractNumber: string;
  title: string;
  customer: string;
  contactPerson: string;
  contractType: 'service' | 'license' | 'maintenance' | 'subscription' | 'consulting';
  status: 'draft' | 'under-review' | 'approved' | 'signed' | 'active' | 'expired' | 'terminated';
  startDate: string;
  endDate: string;
  value: number;
  currency: string;
  renewalDate?: string;
  autoRenewal: boolean;
  renewalNoticePeriod: number;
  assignedTo: string;
  department: string;
  terms: string;
  description: string;
  attachments: Attachment[];
  milestones: Milestone[];
  renewalHistory: RenewalRecord[];
  createdDate: string;
  signedDate?: string;
  lastModified: string;
}

interface Attachment {
  id: string;
  name: string;
  type: string;
  size: number;
  uploadDate: string;
  url: string;
}

interface Milestone {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  status: 'pending' | 'completed' | 'overdue';
  value?: number;
  completedDate?: string;
}

interface RenewalRecord {
  id: string;
  renewalDate: string;
  previousEndDate: string;
  newEndDate: string;
  value: number;
  status: 'renewed' | 'not-renewed' | 'pending';
  notes: string;
}

const ContractManagement: React.FC = () => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [form] = Form.useForm();

  // Mock data
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      const mockContracts: Contract[] = [
        {
          id: '1',
          contractNumber: 'CNT-2024-001',
          title: 'Enterprise Software License Agreement - TechCorp',
          customer: 'TechCorp Solutions',
          contactPerson: 'John Smith',
          contractType: 'license',
          status: 'active',
          startDate: '2024-01-01',
          endDate: '2025-12-31',
          value: 150000,
          currency: 'USD',
          renewalDate: '2025-10-01',
          autoRenewal: true,
          renewalNoticePeriod: 90,
          assignedTo: 'Sarah Johnson',
          department: 'Sales',
          terms: 'Standard enterprise license terms with annual maintenance included.',
          description: 'Software license agreement for enterprise ERP solution including support and maintenance.',
          attachments: [
            {
              id: 'att1',
              name: 'signed-contract.pdf',
              type: 'PDF',
              size: 2048000,
              uploadDate: '2024-01-15',
              url: '/contracts/cnt-2024-001.pdf'
            }
          ],
          milestones: [
            {
              id: 'm1',
              title: 'Implementation Complete',
              description: 'Complete software implementation and go-live',
              dueDate: '2024-03-31',
              status: 'completed',
              value: 50000,
              completedDate: '2024-03-28'
            },
            {
              id: 'm2',
              title: 'Year 1 Support',
              description: 'Provide first year support and maintenance',
              dueDate: '2024-12-31',
              status: 'pending',
              value: 25000
            }
          ],
          renewalHistory: [],
          createdDate: '2023-11-15',
          signedDate: '2024-01-01',
          lastModified: '2024-06-24'
        },
        {
          id: '2',
          contractNumber: 'CNT-2024-002',
          title: 'CRM Subscription Agreement - StartupCo',
          customer: 'Startup Innovations',
          contactPerson: 'Emily Davis',
          contractType: 'subscription',
          status: 'under-review',
          startDate: '2024-07-01',
          endDate: '2025-06-30',
          value: 24000,
          currency: 'USD',
          renewalDate: '2025-04-01',
          autoRenewal: false,
          renewalNoticePeriod: 60,
          assignedTo: 'Mike Chen',
          department: 'Sales',
          terms: 'Monthly subscription with quarterly billing.',
          description: 'Annual CRM subscription with implementation services.',
          attachments: [],
          milestones: [
            {
              id: 'm3',
              title: 'System Setup',
              description: 'Initial CRM setup and configuration',
              dueDate: '2024-07-15',
              status: 'pending',
              value: 5000
            }
          ],
          renewalHistory: [],
          createdDate: '2024-06-01',
          lastModified: '2024-06-24'
        }
      ];
      setContracts(mockContracts);
      setLoading(false);
    }, 1000);
  }, []);

  const getStatusColor = (status: string) => {
    const colors = {
      'draft': 'default',
      'under-review': 'processing',
      'approved': 'warning',
      'signed': 'success',
      'active': 'success',
      'expired': 'error',
      'terminated': 'error'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getContractTypeColor = (type: string) => {
    const colors = {
      'service': 'blue',
      'license': 'green',
      'maintenance': 'orange',
      'subscription': 'purple',
      'consulting': 'cyan'
    };
    return colors[type as keyof typeof colors] || 'default';
  };

  const handleAddContract = () => {
    setSelectedContract(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditContract = (contract: Contract) => {
    setSelectedContract(contract);
    form.setFieldsValue({
      ...contract,
      startDate: dayjs(contract.startDate),
      endDate: dayjs(contract.endDate),
      renewalDate: contract.renewalDate ? dayjs(contract.renewalDate) : null,
    });
    setModalVisible(true);
  };

  const handleViewDetails = (contract: Contract) => {
    setSelectedContract(contract);
    setDetailsDrawerVisible(true);
  };

  const handleSaveContract = async (values: any) => {
    try {
      const contractData = {
        ...values,
        id: selectedContract?.id || Date.now().toString(),
        contractNumber: selectedContract?.contractNumber || `CNT-${dayjs().format('YYYY')}-${String(contracts.length + 1).padStart(3, '0')}`,
        startDate: values.startDate.format('YYYY-MM-DD'),
        endDate: values.endDate.format('YYYY-MM-DD'),
        renewalDate: values.renewalDate ? values.renewalDate.format('YYYY-MM-DD') : null,
        lastModified: dayjs().format('YYYY-MM-DD'),
        attachments: selectedContract?.attachments || [],
        milestones: selectedContract?.milestones || [],
        renewalHistory: selectedContract?.renewalHistory || []
      };

      if (selectedContract) {
        setContracts(contracts.map(contract => 
          contract.id === selectedContract.id ? contractData : contract
        ));
        message.success('Contract updated successfully');
      } else {
        setContracts([...contracts, contractData]);
        message.success('Contract created successfully');
      }

      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('Failed to save contract');
    }
  };

  const columns: ColumnsType<Contract> = [
    {
      title: 'Contract',
      key: 'contract',
      width: 250,
      render: (_, record) => (
        <div>
          <div>
            <Text strong>{record.contractNumber}</Text>
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: 2 }}>
            {record.title}
          </div>
          <div style={{ marginTop: 4 }}>
            <Tag color={getContractTypeColor(record.contractType)}>
              {record.contractType.toUpperCase()}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 180,
      render: (_, record) => (
        <div>
          <div>{record.customer}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.contactPerson}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status.replace('-', ' ').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: 120,
      render: (value, record) => (
        <Text strong style={{ color: '#1890ff' }}>
          ${value.toLocaleString()} {record.currency}
        </Text>
      ),
      sorter: (a, b) => a.value - b.value,
    },
    {
      title: 'Duration',
      key: 'duration',
      width: 160,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            {dayjs(record.startDate).format('MMM DD, YYYY')} -
          </div>
          <div style={{ fontSize: '12px' }}>
            {dayjs(record.endDate).format('MMM DD, YYYY')}
          </div>
        </div>
      ),
    },
    {
      title: 'Renewal',
      key: 'renewal',
      width: 130,
      render: (_, record) => {
        if (!record.renewalDate) return '-';
        const daysToRenewal = dayjs(record.renewalDate).diff(dayjs(), 'days');
        const isNearRenewal = daysToRenewal <= 90 && daysToRenewal > 0;
        const isPastRenewal = daysToRenewal < 0;
        
        return (
          <div>
            <div style={{ 
              color: isPastRenewal ? '#ff4d4f' : isNearRenewal ? '#faad14' : 'inherit',
              fontSize: '12px'
            }}>
              {dayjs(record.renewalDate).format('MMM DD, YYYY')}
            </div>
            {record.autoRenewal && (
              <Badge status="processing" text="Auto" style={{ fontSize: '10px' }} />
            )}
          </div>
        );
      },
    },
    {
      title: 'Assigned To',
      dataIndex: 'assignedTo',
      key: 'assignedTo',
      width: 130,
      render: (assignedTo) => (
        <Space>
          <Avatar size="small" icon={<UserOutlined />} />
          {assignedTo}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button 
              type="link" 
              icon={<EyeOutlined />} 
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              type="link" 
              icon={<EditOutlined />} 
              onClick={() => handleEditContract(record)}
            />
          </Tooltip>
          <Tooltip title="Download">
            <Button type="link" icon={<DownloadOutlined />} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const filteredContracts = contracts.filter(contract => {
    if (activeTab === 'active' && contract.status !== 'active') return false;
    if (activeTab === 'expiring') {
      const daysToExpiry = dayjs(contract.endDate).diff(dayjs(), 'days');
      if (daysToExpiry > 90 || daysToExpiry < 0) return false;
    }
    if (activeTab === 'expired' && contract.status !== 'expired') return false;
    if (activeTab === 'draft' && !['draft', 'under-review'].includes(contract.status)) return false;
    return true;
  });

  const contractStats = {
    total: contracts.length,
    active: contracts.filter(c => c.status === 'active').length,
    expiring: contracts.filter(c => {
      const daysToExpiry = dayjs(c.endDate).diff(dayjs(), 'days');
      return daysToExpiry <= 90 && daysToExpiry > 0;
    }).length,
    expired: contracts.filter(c => c.status === 'expired').length,
    totalValue: contracts.reduce((sum, contract) => sum + contract.value, 0),
    averageValue: contracts.length > 0 
      ? contracts.reduce((sum, contract) => sum + contract.value, 0) / contracts.length 
      : 0
  };

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>
            <FileTextOutlined style={{ marginRight: 8 }} />
            Contract Management
          </Title>
        </Col>
        <Col>
          <Space>
            <Button icon={<UploadOutlined />}>Import</Button>
            <Button icon={<DownloadOutlined />}>Export</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddContract}>
              Add Contract
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Total Contracts"
              value={contractStats.total}
              prefix={<ContainerOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Active Contracts"
              value={contractStats.active}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Expiring Soon"
              value={contractStats.expiring}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Total Value"
              value={contractStats.totalValue}
              prefix={<DollarOutlined />}
              formatter={(value) => `$${Number(value).toLocaleString()}`}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Contract Categories Tabs */}
      <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginBottom: 16 }}>
        <TabPane tab={`All (${contracts.length})`} key="all" />
        <TabPane tab={`Active (${contracts.filter(c => c.status === 'active').length})`} key="active" />
        <TabPane 
          tab={
            <Badge 
              count={contractStats.expiring}
              offset={[10, 0]}
            >
              Expiring Soon
            </Badge>
          } 
          key="expiring" 
        />
        <TabPane tab={`Draft (${contracts.filter(c => ['draft', 'under-review'].includes(c.status)).length})`} key="draft" />
        <TabPane tab={`Expired (${contracts.filter(c => c.status === 'expired').length})`} key="expired" />
      </Tabs>

      {/* Contracts Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredContracts}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1300 }}
          pagination={{
            //pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 500],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} contracts`,
          }}
        />
      </Card>

      {/* Add/Edit Contract Modal */}
      <Modal
        title={selectedContract ? 'Edit Contract' : 'Add New Contract'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={900}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveContract}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="title"
                label="Contract Title"
                rules={[{ required: true, message: 'Please enter contract title' }]}
              >
                <Input placeholder="e.g., Enterprise Software License Agreement - TechCorp" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="customer"
                label="Customer"
                rules={[{ required: true, message: 'Please enter customer name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="contactPerson"
                label="Contact Person"
                rules={[{ required: true, message: 'Please enter contact person' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="contractType"
                label="Contract Type"
                rules={[{ required: true, message: 'Please select contract type' }]}
              >
                <Select>
                  <Option value="service">Service</Option>
                  <Option value="license">License</Option>
                  <Option value="maintenance">Maintenance</Option>
                  <Option value="subscription">Subscription</Option>
                  <Option value="consulting">Consulting</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select>
                  <Option value="draft">Draft</Option>
                  <Option value="under-review">Under Review</Option>
                  <Option value="approved">Approved</Option>
                  <Option value="signed">Signed</Option>
                  <Option value="active">Active</Option>
                  <Option value="expired">Expired</Option>
                  <Option value="terminated">Terminated</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="assignedTo"
                label="Assigned To"
                rules={[{ required: true, message: 'Please select assignee' }]}
              >
                <Select>
                  <Option value="Sarah Johnson">Sarah Johnson</Option>
                  <Option value="Mike Chen">Mike Chen</Option>
                  <Option value="Alex Thompson">Alex Thompson</Option>
                  <Option value="Lisa Wang">Lisa Wang</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="startDate"
                label="Start Date"
                rules={[{ required: true, message: 'Please select start date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="endDate"
                label="End Date"
                rules={[{ required: true, message: 'Please select end date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="renewalDate" label="Renewal Notice Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="value"
                label="Contract Value"
                rules={[{ required: true, message: 'Please enter contract value' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="currency"
                label="Currency"
                rules={[{ required: true, message: 'Please select currency' }]}
              >
                <Select>
                  <Option value="USD">USD</Option>
                  <Option value="EUR">EUR</Option>
                  <Option value="GBP">GBP</Option>
                  <Option value="CAD">CAD</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Contract description..." />
          </Form.Item>

          <Form.Item name="terms" label="Terms & Conditions">
            <TextArea rows={4} placeholder="Contract terms and conditions..." />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedContract ? 'Update Contract' : 'Create Contract'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Contract Details Drawer */}
      <Drawer
        title={selectedContract?.contractNumber || 'Contract Details'}
        width={700}
        open={detailsDrawerVisible}
        onClose={() => setDetailsDrawerVisible(false)}
      >
        {selectedContract && (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Card size="small" title="Status">
                  <div style={{ textAlign: 'center' }}>
                    <Tag color={getStatusColor(selectedContract.status)} style={{ fontSize: '14px', padding: '4px 12px' }}>
                      {selectedContract.status.replace('-', ' ').toUpperCase()}
                    </Tag>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" title="Type">
                  <div style={{ textAlign: 'center' }}>
                    <Tag color={getContractTypeColor(selectedContract.contractType)} style={{ fontSize: '14px', padding: '4px 12px' }}>
                      {selectedContract.contractType.toUpperCase()}
                    </Tag>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" title="Value">
                  <div style={{ textAlign: 'center' }}>
                    <Statistic
                      value={selectedContract.value}
                      prefix="$"
                      suffix={selectedContract.currency}
                      formatter={(value) => Number(value).toLocaleString()}
                      valueStyle={{ fontSize: '18px', color: '#1890ff' }}
                    />
                  </div>
                </Card>
              </Col>
            </Row>

            <Divider orientation="left">Contract Information</Divider>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <p><strong>Title:</strong> {selectedContract.title}</p>
                <p><strong>Customer:</strong> {selectedContract.customer}</p>
                <p><strong>Contact Person:</strong> {selectedContract.contactPerson}</p>
                <p><strong>Assigned To:</strong> {selectedContract.assignedTo}</p>
              </Col>
              <Col span={12}>
                <p><strong>Start Date:</strong> {dayjs(selectedContract.startDate).format('MMMM DD, YYYY')}</p>
                <p><strong>End Date:</strong> {dayjs(selectedContract.endDate).format('MMMM DD, YYYY')}</p>
                <p><strong>Department:</strong> {selectedContract.department}</p>
                {selectedContract.renewalDate && (
                  <p><strong>Renewal Date:</strong> {dayjs(selectedContract.renewalDate).format('MMMM DD, YYYY')}</p>
                )}
              </Col>
            </Row>

            <Divider orientation="left">Description</Divider>
            <p>{selectedContract.description}</p>

            <Divider orientation="left">Terms & Conditions</Divider>
            <p>{selectedContract.terms}</p>

            <Divider orientation="left">Milestones</Divider>
            {selectedContract.milestones.length > 0 ? (
              <List
                dataSource={selectedContract.milestones}
                renderItem={(milestone) => (
                  <List.Item>
                    <List.Item.Meta
                      title={milestone.title}
                      description={
                        <div>
                          <div>{milestone.description}</div>
                          <div style={{ marginTop: 4 }}>
                            <Tag color={milestone.status === 'completed' ? 'green' : milestone.status === 'overdue' ? 'red' : 'orange'}>
                              {milestone.status.toUpperCase()}
                            </Tag>
                            <span style={{ marginLeft: 8 }}>
                              Due: {dayjs(milestone.dueDate).format('MMM DD, YYYY')}
                            </span>
                            {milestone.value && (
                              <span style={{ marginLeft: 8 }}>
                                Value: ${milestone.value.toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Alert message="No milestones defined" type="info" showIcon />
            )}

            <div style={{ marginTop: 24 }}>
              <Space>
                <Button type="primary" onClick={() => handleEditContract(selectedContract)}>
                  Edit Contract
                </Button>
                <Button icon={<DownloadOutlined />}>Download</Button>
                <Button icon={<FileTextOutlined />}>Generate Report</Button>
                <Button icon={<ScheduleOutlined />}>Schedule Renewal</Button>
              </Space>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default ContractManagement;
